import { supabasePublicKey, supabaseUrl, publicEnvErrors } from "@/config/publicEnv";
import { useEffect, useState } from "react";
import { logError, logInfo } from "@/lib/errors";

interface HealthCheckResult {
  passed: boolean;
  checks: {
    buildId: boolean;
    buildIntegrity: boolean;
    runtimeConfig: boolean;
    cssLoaded: boolean;
    supabaseEnv: boolean;
  };
  details: string[];
}

export const useBuildHealthCheck = (): HealthCheckResult | null => {
  const [result, setResult] = useState<HealthCheckResult | null>(null);

  useEffect(() => {
    // Run non-blocking: defer DOM/CSS checks until after first paint
    // using requestIdleCallback so they don't block FCP.
    const run = () => {
      const checks = {
        buildId: false,
        buildIntegrity: false,
        runtimeConfig: false,
        cssLoaded: false,
        supabaseEnv: false,
      };
      const details: string[] = [];

      // Check 1: Build ID meta tag exists
      const buildIdMeta = document.querySelector('meta[name="build-id"]');
      checks.buildId = !!buildIdMeta?.getAttribute('content');
      if (!checks.buildId) details.push('Missing build-id meta tag');

      // Check 2: Build integrity marker
      const integrityMeta = document.querySelector('meta[name="build-integrity"]');
      checks.buildIntegrity = integrityMeta?.getAttribute('content') === 'rack-role-verified';
      if (!checks.buildIntegrity) details.push('Missing or invalid build-integrity marker');

      // Check 3: Runtime config loaded
      const windowWithEnv = window as unknown as { __RACKANDROLE_PUBLIC_ENV__?: unknown };
      checks.runtimeConfig =
        typeof windowWithEnv.__RACKANDROLE_PUBLIC_ENV__ !== 'undefined' ||
        (typeof import.meta !== 'undefined' && !!import.meta.env?.VITE_SUPABASE_URL);
      if (!checks.runtimeConfig) details.push('Runtime configuration not loaded');

      // Check 4: Supabase env is available
      checks.supabaseEnv = Boolean(supabaseUrl && supabasePublicKey);
      if (!checks.supabaseEnv) {
        if (publicEnvErrors.length > 0) {
          details.push(...publicEnvErrors);
        } else {
          details.push('Supabase environment variables are not configured');
        }
      }

      // Check 5: CSS loaded (check for Tailwind classes)
      // Only run DOM mutation inside idle callback — no style recalc at mount time
      const testEl = document.createElement('div');
      testEl.className = 'hidden';
      document.body.appendChild(testEl);
      const computed = getComputedStyle(testEl);
      checks.cssLoaded = computed.display === 'none';
      document.body.removeChild(testEl);
      if (!checks.cssLoaded) details.push('CSS not properly loaded');

      const passed = Object.values(checks).every(Boolean);

      if (!passed) {
        logError("buildHealthCheck", new Error("Health check failed"), { details });
      } else {
        logInfo("buildHealthCheck", "All checks passed");
      }

      setResult({ passed, checks, details });
    };

    // Use requestIdleCallback when available to defer until after first paint,
    // falling back to a short setTimeout for Safari/older browsers.
    if (typeof requestIdleCallback !== 'undefined') {
      const handle = requestIdleCallback(run, { timeout: 5000 });
      return () => cancelIdleCallback(handle);
    } else {
      const t = setTimeout(run, 100);
      return () => clearTimeout(t);
    }
  }, []);

  return result;
};
